import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Session, User } from '@supabase/supabase-js';
import { useLanguage } from '@/contexts/LanguageContext';
import { trackAnalyticsEvent } from '@/hooks/useAnalytics';
import { trackAddToCart, discountedPrice } from '@/lib/gtm';

export interface CartItem {
  id: string;
  name: string;
  brand: string;
  price: number;
  image: string;
  quantity: number;
  requires_consultation?: boolean;
  hide_price_without_consultation?: boolean;
  discount_percentage?: number;
  original_price?: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (product: Omit<CartItem, 'quantity'>) => Promise<void>;
  removeFromCart: (id: string) => Promise<void>;
  updateQuantity: (id: string, quantity: number) => Promise<void>;
  clearCart: (silent?: boolean) => Promise<void>;
  getTotalPrice: () => number;
  getTotalItems: () => number;
  createOrder: (shippingAddress: any, paymentMethod: string, loyaltyPointsUsed?: number, discountCodes?: Array<{id: string; code: string; discount_type: string; discount_value: number; discount_amount: number}>) => Promise<string | null>;
  loading: boolean;
  user: User | null;
  hasConsultationItems: () => boolean;
  hasUnapprovedConsultationItems: () => Promise<boolean>;
  isConsultationApproved: (productId: string) => Promise<boolean>;
  refreshCartStatus: () => void;
  forceRefreshCart: () => Promise<void>;
  manualClearCart: () => Promise<void>;
  isOrderCompleting: boolean;
  setIsOrderCompleting: (value: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isOrderCompleting, setIsOrderCompleting] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const loadCartFromDatabase = async (userToLoad?: any) => {
    const currentUser = userToLoad || session?.user || user;
    console.log('loadCartFromDatabase called with user:', currentUser?.id);
    if (!currentUser) {
      console.log('No user found, aborting cart load');
      return;
    }

    try {
      console.log('Starting cart load from database for user:', currentUser.id);
      setLoading(true);
      
      // Get cart items without join - we'll fetch product details separately
      const { data: cartData, error } = await supabase
        .from('cart_items')
        .select('id, product_id, quantity')
        .eq('user_id', currentUser.id);

      console.log('Database query result:', { cartData, error });

      if (error) {
        console.error('Error loading cart:', error);
        toast({
          title: "Klaida",
          description: "Nepavyko užkrauti krepšelio",
          variant: "destructive",
        });
        return;
      }

      // Get product details separately
      if (cartData && cartData.length > 0) {
        const productIds = cartData.map(item => item.product_id);
        const { data: productsData, error: productsError } = await (supabase as any)
          .from('products_public')
          .select('id, name, brand, price, image, requires_consultation, hide_price_without_consultation, discount_percentage')
          .in('id', productIds);

        if (productsError) {
          console.error('Error loading products:', productsError);
          toast({
            title: "Klaida",
            description: "Nepavyko užkrauti prekių informacijos",
            variant: "destructive",
          });
          return;
        }

        // Combine cart and product data
        const cartDataWithProducts = cartData.map(cartItem => ({
          ...cartItem,
          products: productsData?.find(product => product.id === cartItem.product_id) || {
            id: cartItem.product_id,
            name: 'Unknown Product',
            brand: 'Unknown Brand',
            price: 0,
            image: '',
            requires_consultation: false,
            discount_percentage: 0
          }
        }));

        const cartItems: CartItem[] = cartDataWithProducts.map((item: any) => ({
          id: item.product_id,
          name: item.products?.name || 'Unknown Product',
          brand: item.products?.brand || 'Unknown Brand',
          price: item.products?.price || 0,
          image: item.products?.image || '/placeholder.svg',
          quantity: item.quantity,
          requires_consultation: item.products?.requires_consultation || false,
          hide_price_without_consultation: item.products?.hide_price_without_consultation || false,
          discount_percentage: item.products?.discount_percentage || 0,
        }));

        console.log('Cart items loaded with brands:', cartItems.map(item => ({ id: item.id, name: item.name, brand: item.brand })));
        console.log('Loaded cart items:', cartItems);
        setItems(cartItems);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize auth state
  useEffect(() => {
    let hasLoadedCart = false;
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Load cart when user is authenticated and cart hasn't been loaded yet
        if (session?.user && !hasLoadedCart) {
          console.log('Loading cart due to auth event:', event);
          hasLoadedCart = true;
          setTimeout(() => {
            loadCartFromDatabase(session.user);
          }, 0);
        } else if (!session?.user) {
          // Clear cart when user logs out
          console.log('User logged out, clearing cart...');
          hasLoadedCart = false;
          setItems([]);
        }
      }
    );

    // Check for existing session immediately
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user && !hasLoadedCart) {
        console.log('Loading cart from initial session');
        hasLoadedCart = true;
        loadCartFromDatabase(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const addToCart = async (product: Omit<CartItem, 'quantity'>) => {
    if (!user) {
      toast({
        title: t.cartMessages.loginRequired,
        description: t.cartMessages.loginToAddToCart,
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      // Check if item already exists in cart
      const existingItem = items.find(item => item.id === product.id);
      
      if (existingItem) {
        // Update quantity
        await updateQuantity(product.id, existingItem.quantity + 1);
        // GA4 e-commerce: one more unit of an existing cart item
        trackAddToCart({
          item_id: existingItem.id,
          item_name: existingItem.name,
          item_brand: existingItem.brand,
          price: discountedPrice(existingItem.price, existingItem.discount_percentage),
          quantity: 1,
        });
      } else {
        // Get complete product data including consultation requirements and discount
        const { data: productData, error: productError } = await (supabase as any)
          .from('products_public')
          .select('requires_consultation, hide_price_without_consultation, discount_percentage')
          .eq('id', product.id)
          .single();
        
        if (productError) {
          console.error('Error fetching product data:', productError);
        }
        
        // Add new item
        const { error } = await supabase
          .from('cart_items')
          .insert({
            user_id: user.id,
            product_id: product.id,
            quantity: 1
          });

        if (error) {
          // Handle the duplicate key error gracefully
          if (error.code === '23505') {
            // Refresh cart to ensure UI is in sync
            await loadCartFromDatabase();
            toast({
              title: "Prekė jau krepšelyje",
              description: "Prekė jau yra jūsų krepšelyje",
              variant: "destructive",
            });
          } else {
            console.error('Error adding to cart:', error);
            toast({
              title: "Klaida",
              description: "Nepavyko pridėti prekės į krepšelį",
              variant: "destructive",
            });
          }
          return;
        }

        // Add to local state with complete product data including consultation flag and discount
        const newItem = { 
          ...product, 
          quantity: 1,
          requires_consultation: productData?.requires_consultation || false,
          hide_price_without_consultation: productData?.hide_price_without_consultation || false,
          discount_percentage: productData?.discount_percentage || 0
        };
        console.log('Adding new item to cart with consultation flag:', newItem);
        setItems(prev => [...prev, newItem]);
        trackAnalyticsEvent('cart_add', { product_id: product.id, product_name: product.name, brand: product.brand });
        // GA4 e-commerce: new cart item
        trackAddToCart({
          item_id: product.id,
          item_name: product.name,
          item_brand: product.brand,
          price: discountedPrice(product.price, newItem.discount_percentage),
          quantity: 1,
        });
        const { dismiss } = toast({
          title: t.cart.addedToCart,
          description: `${product.name} ${t.cart.productAddedToCart}`,
          action: (
            <Button variant="outline" size="sm" onClick={() => {
              // Dismiss the toast
              dismiss();
              // Find and click the cart button to open the cart sheet
              const cartTrigger = document.querySelector('[data-testid="cart-trigger"]') as HTMLButtonElement;
              if (cartTrigger) {
                cartTrigger.click();
              }
            }}>
              {t.cart.viewCart}
            </Button>
          )
        });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (id: string) => {
    if (!user) return;

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', id);

      if (error) {
        console.error('Error removing from cart:', error);
        toast({
          title: "Klaida",
          description: "Nepavyko pašalinti prekės iš krepšelio",
          variant: "destructive",
        });
        return;
      }

      setItems(prev => prev.filter(item => item.id !== id));
      toast({
        title: "Pašalinta",
        description: "Prekė pašalinta iš krepšelio",
      });
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (id: string, quantity: number) => {
    if (!user) return;

    if (quantity <= 0) {
      await removeFromCart(id);
      return;
    }

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('cart_items')
        .update({ quantity })
        .eq('user_id', user.id)
        .eq('product_id', id);

      if (error) {
        console.error('Error updating quantity:', error);
        toast({
          title: "Klaida",
          description: "Nepavyko atnaujinti kiekio",
          variant: "destructive",
        });
        return;
      }

      setItems(prev =>
        prev.map(item =>
          item.id === id ? { ...item, quantity } : item
        )
      );
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async (silent: boolean = false) => {
    if (!user) {
      console.log('Cannot clear cart: no user logged in');
      return;
    }

    try {
      setLoading(true);
      
      console.log('Clearing cart for user:', user.id, 'Current items count:', items.length);
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', user.id);

      if (error) {
        console.error('Error clearing cart from database:', error);
        if (!silent) {
          toast({
            title: "Klaida",
            description: "Nepavyko išvalyti krepšelio",
            variant: "destructive",
          });
        }
        return;
      }

      console.log('Cart cleared successfully from database, updating local state');
      setItems([]);
      
      // Force a refresh from database to ensure sync
      setTimeout(async () => {
        await loadCartFromDatabase();
        console.log('Cart reloaded after clearing');
      }, 100);
      
      if (!silent) {
        toast({
          title: t.cartMessages.cartCleared,
          description: t.cartMessages.allItemsRemoved,
        });
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      if (!silent) {
        toast({
          title: "Klaida", 
          description: "Nepavyko išvalyti krepšelio",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async (shippingAddress: any, paymentMethod: string, loyaltyPointsUsed: number = 0, discountCodes: Array<{id: string; code: string; discount_type: string; discount_value: number; discount_amount: number}> = []): Promise<string | null> => {
    if (!user || items.length === 0) return null;

    try {
      setLoading(true);
      
      // Fetch shipping settings from database
      console.log('CartContext: Fetching shipping settings for order creation...');
      const { data: shippingSettings, error: shippingError } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'shipping_config')
        .maybeSingle();

      console.log('CartContext: Shipping settings fetch result:', { shippingSettings, shippingError });

      if (shippingError && shippingError.code !== 'PGRST116') {
        console.error('CartContext: Error fetching shipping settings:', shippingError);
        toast({
          title: "Klaida",
          description: "Nepavyko gauti pristatymo nustatymų",
          variant: "destructive",
        });
        return null;
      }

      const shippingConfig = shippingSettings?.value as any;
      const standardShippingCost = shippingConfig?.standard_shipping_cost || 2.90;
      const freeShippingThreshold = shippingConfig?.free_shipping_threshold || 30;
      
      console.log('CartContext: Using shipping config:', { standardShippingCost, freeShippingThreshold });
      
      const subtotal = getTotalPrice();
      const pointsDiscount = loyaltyPointsUsed; // 1 point = 1 euro
      const discountCodesTotal = discountCodes.reduce((sum, d) => sum + d.discount_amount, 0);
      const discountedSubtotal = Math.max(0, subtotal - pointsDiscount - discountCodesTotal);
      const isSalonPickup = shippingAddress?.deliveryMethod === 'salon_pickup';
      const shippingCost = isSalonPickup ? 0 : (subtotal >= freeShippingThreshold ? 0 : standardShippingCost);
      const totalAmount = discountedSubtotal + shippingCost;

      // Create order
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .insert({
          user_id: user.id,
          total_amount: totalAmount,
          shipping_cost: shippingCost,
          shipping_address: shippingAddress,
          payment_method: paymentMethod,
          status: 'pending',
          payment_status: 'pending',
          customer_first_name: shippingAddress.firstName,
          customer_last_name: shippingAddress.lastName,
          customer_phone: shippingAddress.phone,
          customer_email: shippingAddress.email || user.email,
        })
        .select()
        .single();

      if (orderError) {
        console.error('Error creating order:', orderError);
        toast({
          title: "Klaida",
          description: "Nepavyko sukurti užsakymo",
          variant: "destructive",
        });
        return null;
      }

      // Create order items with proper discount calculation
      const orderItems = items.map(item => {
        let originalPrice = item.price; // item.price is always the original price
        let unitPrice = item.price;
        
        // If there's a discount, calculate the discounted unit price
        if (item.discount_percentage && item.discount_percentage > 0) {
          unitPrice = item.price * (1 - item.discount_percentage / 100); // Apply discount
        }
        
        return {
          order_id: orderData.id,
          product_id: item.id,
          quantity: item.quantity,
          unit_price: unitPrice,
          original_price: originalPrice,
          discount_percentage: item.discount_percentage || 0,
          total_price: unitPrice * item.quantity,
          product_name: item.name,
          product_brand: item.brand,
          product_image: item.image
        };
      });

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) {
        console.error('Error creating order items:', itemsError);
        // Rollback order
        await supabase.from('orders').delete().eq('id', orderData.id);
        toast({
          title: "Klaida",
          description: "Nepavyko sukurti užsakymo",
          variant: "destructive",
        });
        return null;
      }

      // If loyalty points were used, record the spending
      if (loyaltyPointsUsed > 0) {
        const { error: loyaltyError } = await supabase
          .from('loyalty_points')
          .insert({
            user_id: user.id,
            points_earned: 0,
            points_spent: loyaltyPointsUsed,
            order_id: orderData.id,
            description: `Order #${orderData.id.slice(0, 8)} - ${loyaltyPointsUsed} points used for €${loyaltyPointsUsed} discount`
          });

        if (loyaltyError) {
          console.error('Error recording loyalty points spending:', loyaltyError);
          // Don't fail the order creation for this
        }
      }

      // Record discount codes used and increment usage
      if (discountCodes.length > 0) {
        for (const dc of discountCodes) {
          await supabase.from('order_discount_codes').insert({
            order_id: orderData.id,
            discount_code_id: dc.id,
            discount_type: dc.discount_type,
            discount_value: dc.discount_value,
            discount_amount: dc.discount_amount,
          });
          // Increment usage count
          await (supabase as any).rpc('increment_discount_code_usage', { code_id: dc.id });
        }
      }

      // Clear cart after successful order - delay to allow loading state to show
      setTimeout(async () => {
        await clearCart(true);
      }, 100);

      // Send order confirmation email
      try {
        const { error: emailError } = await supabase.functions.invoke('send-order-confirmation', {
          body: {
            to: user.email,
            orderId: orderData.id,
            orderItems: orderItems,
            totalAmount: totalAmount,
            shippingAddress: shippingAddress,
          }
        });

        if (emailError) {
          console.error('Failed to send confirmation email:', emailError);
          // Don't fail the order process if email fails
        } else {
          console.log('Order confirmation email sent successfully');
        }
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
        // Don't fail the order process if email fails
      }

      toast({
        title: "Užsakymas sukurtas",
        description: `Jūsų užsakymas #${orderData.id.slice(0, 8)} sėkmingai sukurtas`,
      });

      return orderData.id;
    } catch (error) {
      console.error('Error creating order:', error);
      toast({
        title: "Klaida",
        description: "Nepavyko sukurti užsakymo",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => {
      const discountedPrice = item.discount_percentage && item.discount_percentage > 0 
        ? item.price * (1 - item.discount_percentage / 100) 
        : item.price;
      return total + (discountedPrice * item.quantity);
    }, 0);
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const hasConsultationItems = () => {
    const result = items.some(item => item.requires_consultation);
    console.log('hasConsultationItems called - items:', items.length, 'result:', result, 'consultation items:', items.filter(item => item.requires_consultation).map(item => ({ name: item.name, requires_consultation: item.requires_consultation })));
    return result;
  };

  const hasUnapprovedConsultationItems = async () => {
    if (!user) return false;
    
    const consultationItems = items.filter(item => item.requires_consultation);
    if (consultationItems.length === 0) return false;

    try {
      // Get approved consultations for this user
      const { data: approvals, error } = await supabase
        .from('consultation_approvals')
        .select('product_id')
        .eq('user_id', user.id)
        .in('product_id', consultationItems.map(item => item.id));

      if (error) {
        console.error('Error checking consultation approvals:', error);
        return true; // Assume unapproved if we can't check
      }

      const approvedProductIds = new Set(approvals?.map(approval => approval.product_id) || []);
      const hasUnapproved = consultationItems.some(item => !approvedProductIds.has(item.id));
      
      console.log('hasUnapprovedConsultationItems:', {
        consultationItems: consultationItems.length,
        approvedProductIds: Array.from(approvedProductIds),
        hasUnapproved
      });

      return hasUnapproved;
    } catch (error) {
      console.error('Error in hasUnapprovedConsultationItems:', error);
      return true; // Assume unapproved if error occurs
    }
  };

  const isConsultationApproved = async (productId: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const { data: approvals, error } = await supabase
        .from('consultation_approvals')
        .select('product_id')
        .eq('user_id', user.id)
        .eq('product_id', productId);

      if (error) {
        console.error('Error checking consultation approval:', error);
        return false;
      }

      return approvals && approvals.length > 0;
    } catch (error) {
      console.error('Error in isConsultationApproved:', error);
      return false;
    }
  };

  const refreshCartStatus = () => {
    // Force re-render by updating a dummy state
    setItems(prevItems => [...prevItems]);
  };

  const forceRefreshCart = async () => {
    console.log('Force refreshing cart from database');
    await loadCartFromDatabase();
  };

  const manualClearCart = async () => {
    console.log('Manual cart clear initiated');
    await clearCart(false); // Show toast for manual clear
  };

  return (
    <CartContext.Provider value={{
      items,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getTotalPrice,
      getTotalItems,
      createOrder,
      loading,
      user,
      hasConsultationItems,
      hasUnapprovedConsultationItems,
      isConsultationApproved,
      refreshCartStatus,
      forceRefreshCart,
      manualClearCart,
      isOrderCompleting,
      setIsOrderCompleting
    }}>
      {children}
    </CartContext.Provider>
  );
};